import { Flex, Spinner, Text } from '@smarterservices/smarter-ui';
import React, { useEffect, useRef, useState } from 'react';
import { completeAndGetNextStep, getStep } from './api';
import {
  applyModalityOptions,
  checkExtension,
  checkPermissions,
  enablePermissions,
  removeModalityOptions,
  renderStepComponent,
  startBatteryMonitor,
  disengageExtension,
} from '../../helpers/helpers';
import { useNavigate } from 'react-router-dom';
import {
  ParamsNotFoundError,
} from '../../helpers/definedErrors';
import PaneViewer from '../../components/PaneViewer';
import Loader from '../../components/Common/Loader';
import useAppStore from '../../stores/app';
import { useLogger } from '@ui/shared/components/Logger';

const Offboarding = ({ isReconnect = false, inSession = false }) => {
  const logger = useLogger();
  const [step, setStep] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isTaskComplete, setIsTaskComplete] = useState(false);
  const [checkedForValidSetup, setCheckedForValidSetup] = useState(false);
  const needsIntroStep = useRef(isReconnect);
  const navigate = useNavigate();
  const stepMetadataRef = useRef({});
  const setStepMetadata = (metadata) => {
    stepMetadataRef.current = metadata;
  };

  const {
    initializeApp,
  } = useAppStore();

  const phase = useAppStore((state) => state.phase);

  useEffect(() => {
      const initialize = async () => {
        try {
          await initializeApp({type: 'session'});
        } catch (error) {
          logger.logError('Offboarding', 'UI', 'Failed to initialize app', error);
          console.error('Failed to initialize app:', error);
          // TODO: Consider navigating to an error page or showing an error state
        }
      };
      initialize();
    }, []);

  // Get the step we are on when the component loads
  useEffect(() => {
    // Setup the listener to listen for extension messages
    const handleMessage = (event) => {
      if (
        event.source === window &&
        event.data?.source === 'SmarterProctoringExtensionMessenger'
      ) {
        const payload = event.data?.payload;
        if (!payload || !payload?.message) return;

        if (payload.message === 'pushToNextStep') {
          handleStep(false);
        }
      }
    };
    window.addEventListener('message', handleMessage);
    // Setup offboarding and set the first step
    const initializeOffboarding = async () => {
      // Ensure the extension is fully disengaged...
      const disengaged = await disengageExtension();
      if (disengaged) {
        // log that we had to disengage the extension at this phase...
        logger.logInfo('Offboarding', 'UI', 'Disengaged extension at offboarding initialization');
      }

      const storedParams = JSON.parse(sessionStorage.getItem('spParams'));
      if (!storedParams) {
        // We do not have stored params, so we need to get them from the URL
        const url = new URL(window.location.href);
        const params = new Proxy(new URLSearchParams(url.search), {
          get: (searchParams, prop) => searchParams.get(prop),
        });

        if (
          !params ||
          !params.sessionSid ||
          !params.examSid ||
          !params.installSid ||
          !params.token
        ) {
          // We do not have all the necessary params, so redirect them to a page that says that
          navigate('/missing-params');
          return;
        }

        // Store the params in sessionStorage to be used other places
        const paramsToStore = {
          sessionSid: params.sessionSid,
          examSid: params.examSid,
          installSid: params.installSid,
          token: params.token,
        };

        sessionStorage.setItem('spParams', JSON.stringify(paramsToStore));
        // Clear the query params from the URL
        url.search = '';
        window.history.replaceState({}, document.title, url);
      }
      if (!inSession) {
        const isActive = await checkIfSessionActiveElsewhere(storedParams);
        if (isActive) return;
      }
      await handleStep(false);
    };
    initializeOffboarding();

    // remove the window listener on unmount
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [navigate]);

  async function checkIfSessionActiveElsewhere(params) {
    /*
      TODO: This is a potential to do item if we want to enforce only one offboarding at a time. 
      This is being left as stubbed in to match what onboarding does in the event we want to enforce
      this rule.
    */

    return false;
  }

  const handleConfigurations = async (configurations = []) => {
    if (!Array.isArray(configurations)) {
      return;
    }
    // make sure that every item in the array is an object
    const isValid = configurations.every(
      (item) => typeof item === 'object' && item !== null
    );
    if (!isValid) {
      return;
    }
    for (const configuration of configurations) {
      // take an action based on the passed in configurations
      // TODO: figure out what to do when any of these fail by coming back false
      if (configuration.name === 'setPermissions') {
        const permissionsEnabled = await checkPermissions();
        if (!permissionsEnabled) {
          const success = await enablePermissions();
          console.log('Successfully Enabled Permissions:', success);
        }
      } else if (configuration.name === 'applyModalityOptions') {
        const success = await applyModalityOptions(
          configuration.modalityOptions
        );
        console.log('Successfully applied Modality Options:', {
          success,
          modalityOptions: configuration.modalityOptions,
        });
      } else if (configuration.name === 'removeModalityOptions') {
        const success = await removeModalityOptions(
          configuration.modalityOptions
        );
        console.log('Successfully removed Modality Options:', {
          success,
          modalityOptions: configuration.modalityOptions,
        });
      } else if (configuration.name === 'startBatteryMonitor') {
        const success = await startBatteryMonitor(configuration.config);
        console.log('Started Battery Monitor:', {
          success,
          config: configuration.config,
        });
      }
    }
  };

  const isInformationalStep = (stepToCheck) => {
    const informationSteps = [
      'DownloadExtension',
      'ReconnectIntro',
      'ReconnectIntroInSession',
      'UnsupportedDevice',
      'UnsupportedBrowser',
    ];
    return informationSteps.includes(stepToCheck.step.id);
  };

  const getInformationalStepToLoad = async (newStep, params = null) => {
    if (!params) {
      params = JSON.parse(sessionStorage.getItem('spParams'));
      if (!params) {
        console.error('No params were found!');
        throw new ParamsNotFoundError();
      }
    }

    // Informational steps do not always have next buttons, so we are responsible for completing them if needed
    const isNewStepInformational = isInformationalStep(newStep);

    if (isNewStepInformational) {
      // We had a different informational step that we need to complete before moving forward
      newStep = await completeAndGetNextStep(newStep.step.journeyStepId, {}, params);
      // nothing to return because this will push to the next step...
      return newStep;
    } else {
      return newStep;
    }
  };

  const handleStep = async (completeStep = false) => {
    const params = JSON.parse(sessionStorage.getItem('spParams'));
    if (!params) {
      navigate('/missing-params');
      return;
    }
    setLoading(true);
    let newStep = null;
    if (completeStep) {
      // Remove the next arrow
      setIsTaskComplete(false);
      // Complete the current step, and load the next
      newStep = await completeAndGetNextStep(
        step.journeyStepId,
        stepMetadataRef.current,
        params
      );
      // Clear the metadata for the step
      setStepMetadata({});
    } else {
      // Load the current step
      newStep = await getStep(params);
    }

    // Check if we need to load an informational step
    try {
      const informationalStep = await getInformationalStepToLoad(newStep, params);
      if (informationalStep) {
        // Load the informational step instead
        newStep = informationalStep;
      }

      if (newStep?.configurations) {
        await handleConfigurations(newStep.configurations);
      }
      // Set new task and remove the next arrow
      setStep(newStep.step);
      useAppStore.getState().setStep(newStep.step);
      useAppStore.getState().setPhase(newStep.phase);
      setIsTaskComplete(false);
    } catch (error) {
      // Specifically handle ParamsNotFoundError
      if (error instanceof ParamsNotFoundError) {
        console.error('Session parameters not found during step navigation:', error);
        // Navigate to missing params page as a fallback
        navigate('/missing-params');
        return;
      } else {
        // Handle other errors
        console.error('Error during step navigation:', error);
        // You could add additional error handling here if needed
      }
    } finally {
      // Stop the loading bar regardless of success or failure
      setLoading(false);
    }
  };

  return (
    <>
      {step && (
        <PaneViewer
          step={step}
          phase={phase}
          nextEnabled={isTaskComplete}
          inSession={inSession}
          onNext={() => handleStep(true)}
          setIsTaskComplete={setIsTaskComplete}
      >
        {loading && (
          <Flex
          direction="column"
          alignItems="center"
          justifyItems="center"
          height="100%"
        >
          <Loader text="Loading..." size="large" />
        </Flex>
        )}
        {(!loading && !step) && (
          <Flex alignItems="center" justifyItems="center">
            <Text>No steps available</Text>
          </Flex>
        )}
        {(!loading && step) && renderStepComponent(
          step,
          setIsTaskComplete,
          handleStep,
          setStepMetadata,
          inSession
        )}
      </PaneViewer>
      )}
    </>
  );
};

export default Offboarding;
